import { max, min } from "d3-array";
import { scaleLinear, scaleOrdinal } from "d3-scale";
import { timeParse } from "d3-time-format";
import { constant } from "fp-ts/lib/function";
import React from "react";
import { invertSparseArray } from "../../lib";
import * as M from "../../materials";
import { fontChartLabel as VALUE_FONT } from "../../materials/typography/fonts";
import { Ar, Eq, pipe } from "../../prelude";
import type { LineChartProps } from "./Lines";
import { Y_CONNECTOR, Y_CONNECTOR_PADDING } from "./shared/constants";
import { createTextGauger } from "./textGauger";
import { foldMarkStyle, type MarkStyle } from "./types";
import { calculateAxis, deduplicate, groupBy, runSort, type SortType, subsup, unsafeDatumFn } from "./utils";

export { Y_CONNECTOR, Y_CONNECTOR_PADDING };

// `Line`/`Slope` (the only ways to reach `useLayout`/`LineChart`) unconditionally default
// every one of these fields before passing props down, so they're never actually
// optional here even though the public LineChartProps<A> declares them as such.
// `xSort` isn't part of the public contract at all (nothing passes it), but is kept working.
export type LayoutProps<A> = LineChartProps<A> & {
  x: string;
  xScale: "time" | "ordinal" | "linear";
  timeParse: string;
  timeFormat: string;
  numberFormat: string;
  zero: boolean;
  unit: string;
  startValue: boolean;
  endLabel: boolean;
  endDy: string;
  minInnerWidth: number;
  columns: number;
  height: number;
  colorLegend: boolean;
  yNice: number;
  xSort?: SortType;
};

export interface ParsedDatum<A> {
  datum: A;
  x: $Unexpressable; // depends on xScale/xParser, which are only known at runtime
  value: $FixMe; // number | null in practice; kept loose since d3/format helpers coerce null fine
  category?: $FixMe;
}

interface LineGroupData<A> {
  key: string | number;
  values: ParsedDatum<A>[];
}

// Structurally identical to (the unexported) Section<A> in ./Lines, so it's
// interchangeable with props.makeSections's real declared type.
interface LineSection<A> {
  line: Array<{ datum: A }>;
  style?: { strokeDasharray?: string };
}

const COLUMN_TITLE_HEIGHT = 24;
const AXIS_TOP_HEIGHT = 24;
const AXIS_BOTTOM_HEIGHT = 24;

const valueGauger = createTextGauger(VALUE_FONT, {
  dimension: "width",
  html: true,
});
// const labelGauger = createTextGauger(LABEL_FONT, {
//   dimension: "width",
//   html: true
// });

const mkLineColor = (markStyle: MarkStyle, fallback: () => string) =>
  foldMarkStyle(markStyle, {
    normal: fallback,
    muted: fallback,
    reference: () => M.grayscalePalette[5],
  });

const useLayout = <A>(props: LayoutProps<A>) => {
  const { values, mini, yAnnotations, yAxisLabelPadding } = props;
  const makeSections: (xs: Array<{ datum: A }>) => Array<LineSection<A>> = props.makeSections ?? ((line) => [{ line }]);
  let rawData = values;
  if (props.filter) {
    const filter = unsafeDatumFn(props.filter);
    rawData = rawData.filter(filter);
  }
  let xParser: (input: $FixMe) => unknown = (x) => x;
  if (props.xScale === "time") {
    xParser = timeParse(props.timeParse ?? "%Y");
  }
  let data: ParsedDatum<A>[] = rawData.map(
    (d): ParsedDatum<A> => ({
      datum: d,
      x: xParser((d as $FixMe)[props.x]),
      value: (d as $FixMe).value,
    }),
  );
  if (props.category) {
    const categorize = unsafeDatumFn(props.category);
    data.forEach((d) => {
      d.category = categorize(d.datum);
    });
  }
  const xAccessor = React.useMemo(() => (d: ParsedDatum<A>) => d.x, []);
  runSort(props.xSort, data, xAccessor);

  let groupedInput: LineGroupData<A>[];
  if (props.columnFilter) {
    groupedInput = props.columnFilter.map(({ test, title }) => {
      const filter = unsafeDatumFn(test);
      return {
        key: title,
        values: data.filter((d) => filter(d.datum)),
      };
    });
    data = groupedInput.reduce((all: ParsedDatum<A>[], group) => all.concat(group.values), []);
  } else {
    groupedInput = groupBy(data, (d) => (d.datum as $FixMe)[props.column as $FixMe]);
  }
  const lineGroup = props.category
    ? (d: ParsedDatum<A>) => d.category
    : (d: ParsedDatum<A>) => (d.datum as $FixMe)[props.color as $FixMe];

  const paddingTop = AXIS_TOP_HEIGHT + (props.column || props.columnFilter ? COLUMN_TITLE_HEIGHT : 0);
  const paddingBottom = AXIS_BOTTOM_HEIGHT;
  const innerHeight = mini ? props.height - paddingTop - paddingBottom : props.height;
  const columnHeight = innerHeight + paddingTop + paddingBottom;

  let yValues: $FixMe[] = props.yTicks ? props.yTicks : data.map((d) => d.value);
  if (yAnnotations) {
    yValues = yValues.concat(yAnnotations.map((d) => (d.type === "line" ? d.value : undefined)));
  }
  const minValue = min(yValues);
  const y = scaleLinear()
    .domain([props.zero ? Math.min(0, minValue) : minValue, max(yValues)])
    .nice(props.yNice)
    .range([innerHeight + paddingTop, paddingTop]);
  const colorAccessor = props.color
    ? (d: ParsedDatum<A>) => (d.datum as $FixMe)[props.color as $FixMe]
    : (d: ParsedDatum<A>) => d.category;

  // The `props.colorScale` branch's value is unused: `color` below short-circuits before
  // `colorRange` is consulted whenever `props.colorScale` is set.
  const colorRange: $FixMe = props.colorScale
    ? props.colorScale
    : M.colorRanges[props.colorRange as keyof M.ColorRanges] || props.colorRange || M.colorRanges.discrete;

  const colorValues: $FixMe[] = props.colorScale
    ? props.colorScale.range()
    : ([] as unknown[]).concat(data.map(colorAccessor)).filter(deduplicate).filter(Boolean);

  const color = props.colorScale || scaleOrdinal(colorRange).domain(colorValues);

  const { unit, tLabel } = props;
  const yAxis = calculateAxis(props.numberFormat, tLabel, y.domain(), unit);
  const { format: yFormat } = yAxis;

  const startValue = !mini && props.startValue;
  const endLabel = props.endLabel && colorValues.length > 0;

  let startValueSizes: number[] = [];
  let endValueSizes: number[] = [];

  const mkMarkStyle = props.markStyle ? props.markStyle : constant<MarkStyle>("normal");
  const mkIsFaded = props.isFaded ? props.isFaded : constant<boolean>(false);

  const labelFilter = props.labelFilter ? unsafeDatumFn(props.labelFilter) : () => true;
  const groupedLines = groupedInput
    .map(({ values, key }) => ({
      key,
      values: groupBy(values, lineGroup),
    }))
    .map(({ values: lines, key }) => {
      const linesWithLabels = lines
        .filter(({ values: line }) => line.some((d) => d.value !== null))
        .map(({ values: line }) => {
          const start = line[0];
          let end = line[line.length - 1];
          // Find the latest data point with a value
          for (let i = line.length - 1; i >= 0; --i) {
            if (line[i].value !== null) {
              end = line[i];
              break;
            }
          }

          const label = labelFilter(start.datum);
          const markStyle = mkMarkStyle(start.datum);
          const isFaded = mkIsFaded(start.datum);

          return {
            sections: makeSections(line).map(({ line, style }) => ({
              line,
              // We only compute this when requested because it's expensive.
              // `line` is narrowed to `{datum: A}` by the public makeSections contract, but
              // section items are always drawn from the fully-shaped parsed data above.
              lineDefined: props.connectLines ? invertSparseArray(line as $FixMe) : line,
              style,
            })),
            start,
            end,
            isFaded,
            markStyle,
            lineColor: mkLineColor(markStyle, () => color(colorAccessor(start)) as string),
            startValue: label && startValue && yFormat(start.value),
            endValue: label && yFormat(end.value),
            endLabel,
          };
        });

      if (startValue) {
        startValueSizes = startValueSizes.concat(
          linesWithLabels.map((line) => (line.startValue ? valueGauger(line.startValue) : 0)),
        );
      }
      if (endLabel) {
        endValueSizes = endValueSizes.concat(
          linesWithLabels.map((line) => (line.endValue ? valueGauger(line.endValue) : 0)),
        );
      }

      return {
        key,
        values: [
          ...linesWithLabels.filter((line) =>
            foldMarkStyle(line.markStyle, {
              normal: () => false,
              muted: () => true,
              reference: () => false,
            }),
          ),
          ...linesWithLabels.filter((line) =>
            foldMarkStyle(line.markStyle, {
              normal: () => false,
              muted: () => false,
              reference: () => true,
            }),
          ),
          ...linesWithLabels.filter((line) =>
            foldMarkStyle(line.markStyle, {
              normal: () => true,
              muted: () => false,
              reference: () => false,
            }),
          ),
        ],
      };
    });

  let colorLegend = !mini && colorValues.length > 0;
  let paddingLeft = 0;
  let paddingRight = 0;

  const whiteSpacePadding = groupedLines.length > 1 && (props.columns ?? 0) > 1 ? 15 : 0;

  if (!mini || endLabel) {
    const yConnectorSize = Y_CONNECTOR + Y_CONNECTOR_PADDING * 2;
    const startValueSize = startValue ? Math.ceil((max(startValueSizes) || 0) + yConnectorSize) : 0;
    const endValueSize = Math.ceil((max(endValueSizes) || 0) + yConnectorSize);
    if (startValue) {
      paddingLeft = paddingRight = Math.max(startValueSize, endValueSize) + whiteSpacePadding;
    } else {
      paddingRight = endValueSize + whiteSpacePadding;
    }
    if (props.paddingRight !== undefined) {
      paddingRight = props.paddingRight + whiteSpacePadding;
    }
    if (props.paddingLeft !== undefined) {
      paddingLeft = props.paddingLeft + whiteSpacePadding;
    }
  }

  const colorValuesForLegend = pipe(
    data.filter((d) => labelFilter(d.datum)),
    Ar.uniq(Eq.contramap(colorAccessor)(Eq.eqString)),
  );

  const colorLegendValues = colorValuesForLegend.map((value) => {
    const style = mkMarkStyle(value.datum);
    return {
      color: mkLineColor(style, () => color(colorAccessor(value)) as string),
      label: subsup(colorAccessor(value)),
      style,
    };
  });

  const translatedYAnnotations = (yAnnotations || []).map((d) => {
    if (d.type !== "line") {
      return d;
    }
    return {
      ...d,
      formattedValue: yFormat(d.value),
      x: d.x ? xParser(d.x) : undefined,
    };
  });

  return {
    data,
    groupedData: groupedLines,
    xParser,
    xAccessor,
    y,
    yAxis,
    yAxisLabelPadding,
    yAnnotations: translatedYAnnotations,
    yFormat,
    colorLegend,
    colorLegendValues,
    paddingLeft,
    paddingRight,
    columnHeight,
  };
};

export default useLayout;
